import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sun, Moon, AlertCircle, RefreshCw, Search,
  ChevronUp, ChevronDown, ChevronsUpDown, X, Filter,
  TrendingUp, TrendingDown,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ScatterChart, Scatter, ZAxis, ReferenceLine, ResponsiveContainer,
  Cell, ComposedChart, LabelList, Line
} from "recharts";
import { getDadosLuciano } from "../api";
import { GaugeChart } from "../componets/GaugeChart";
import SideBarNavOperacao from "../componets/SideBarNavOperacao";
import { useLocation } from "react-router-dom";
/* ─────────────────────────────────────────────
   Helpers globais (usados dentro e fora do componente)
───────────────────────────────────────────── */
const DEDUCAO = 0.9335;
const normTipo = (v) => String(v || "").trim().toLowerCase();
const isReceita = (t) => normTipo(t) === "receita";
const isCustoTot = (t) => normTipo(t) === "custo total";


const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  danger: "#EF4444",
  row1: isLight ? "#FAF8FF" : "#160D2A",
  row2: isLight ? "#FFFFFF" : "#1A1030",
});
/* ─────────────────────────────────────────────
   Helper para ordenação Cronológica de Mês/Ano
───────────────────────────────────────────── */
const mesParaNumero = {
  "janeiro": 1, "jan": 1,
  "fevereiro": 2, "fev": 2,
  "março": 3, "marco": 3, "mar": 3,
  "abril": 4, "abr": 4,
  "maio": 5, "mai": 5,
  "junho": 6, "jun": 6,
  "julho": 7, "jul": 7,
  "agosto": 8, "ago": 8,
  "setembro": 9, "set": 9,
  "outubro": 10, "out": 10,
  "novembro": 11, "nov": 11,
  "dezembro": 12, "dez": 12
};

const getValorCronologico = (mesAno) => {
  if (!mesAno) return 0;

  // Divide a string "Janeiro/2026" ou "01/2026"
  const [mesStr, anoStr] = String(mesAno).toLowerCase().split("/");

  const ano = parseInt(anoStr, 10) || 0;
  // Tenta converter o mês para número se for "01", senão busca no dicionário
  const mes = parseInt(mesStr, 10) || mesParaNumero[mesStr.trim()] || 0;

  // Retorna um formato YYYYMM (ex: 2026 * 100 + 1 = 202601)
  return ano * 100 + mes;
};
/* ─────────────────────────────────────────────
   MultiSelect
───────────────────────────────────────────── */
function MultiSelect({ options = [], value = [], onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        width: "100%", display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 6,
        padding: "7px 10px", borderRadius: 7, cursor: "pointer",
        background: "transparent", border: "1px solid var(--border)",
        color: "var(--text)", fontSize: 12, fontWeight: 500,
      }}>
        <span style={{ color: value.length ? "var(--text)" : "var(--muted)" }}>
          {value.length ? `${value.length} selecionado(s)` : placeholder}
        </span>
        <ChevronDown size={13} style={{ flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{
          position: "absolute", zIndex: 99, top: "calc(100% + 4px)", left: 0, right: 0,
          background: "var(--card)", border: "1px solid var(--border)",
          borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          maxHeight: 220, overflowY: "auto",
        }}>
          {options.map((opt) => (
            <label key={opt} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "7px 12px", cursor: "pointer", fontSize: 12,
              color: "var(--text)",
              background: value.includes(opt) ? "rgba(124,58,237,0.12)" : "transparent",
            }}>
              <input type="checkbox" checked={value.includes(opt)} onChange={() => toggle(opt)}
                style={{ accentColor: "#7C3AED" }} />
              {opt}
            </label>
          ))}
          {options.length === 0 && (
            <p style={{ padding: 10, fontSize: 12, color: "var(--muted)", margin: 0 }}>Sem opções</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Utilitários
───────────────────────────────────────────── */
const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v ?? 0);
const fmtK = (v) => {
  if (Math.abs(v) >= 1_000_000) return `R$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `R$${(v / 1_000).toFixed(0)}k`;
  return fmtBRL(v);
};
const fmtPct = (v) => `${(v * 100).toFixed(2).replace(".", ",")}%`;

function DifCell({ val }) {
  const pos = val >= 0;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700, fontSize: 13, color: pos ? "#10B981" : "#EF4444" }}>
      {pos ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
      {fmtBRL(val)}
    </span>
  );
}

function ThSort({ label, col, sort, onSort, align = "left" }) {
  const active = sort.col === col;
  return (
    <th onClick={() => onSort(col)} style={{
      padding: "10px 14px", textAlign: align, fontSize: 11,
      fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
      color: active ? "#7C3AED" : "var(--muted)",
      cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
      borderBottom: "2px solid var(--border)", background: "var(--card)",
    }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        {active
          ? sort.dir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />
          : <ChevronsUpDown size={12} style={{ opacity: 0.4 }} />}
      </span>
    </th>
  );
}

/* ─────────────────────────────────────────────
   Card container para gráficos
───────────────────────────────────────────── */
function ChartCard({ title, subtitle, children, C, isLight, style, loading }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: "16px 16px 12px",
      boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
      ...style,
    }}>
      <p style={{ fontSize: 13, fontWeight: 800, color: C.text, margin: "0 0 1px" }}>{title}</p>
      {subtitle && <p style={{ fontSize: 11, color: C.muted, margin: "0 0 12px" }}>{subtitle}</p>}
      <div style={{ marginTop: 10 }}>
        {loading ? <ChartLoader color={C.muted} /> : children}
      </div>
    </div>
  );
}

const ChartLoader = ({ color = "#9585C0", height = 240 }) => (
  <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <RefreshCw size={28} color={color} style={{ animation: "spin 1s linear infinite" }} />
  </div>
);

const CardSkeleton = ({ isLight }) => {
  const shimmerBg = isLight
    ? "linear-gradient(90deg, #F0EBF8 25%, #E0D8F0 50%, #F0EBF8 75%)"
    : "linear-gradient(90deg, #1A1030 25%, #2E2050 50%, #1A1030 75%)";
  const bar = (w, h = 10, mt = 0) => (
    <div style={{
      width: w, height: h, borderRadius: 6, marginTop: mt,
      background: shimmerBg, backgroundSize: "200% 100%",
      animation: "shimmer 1.4s ease-in-out infinite",
    }} />
  );
  return (
    <div style={{ padding: "14px 18px" }}>
      {bar("55%", 9)}
      {bar("70%", 22, 10)}
      {bar("45%", 8, 8)}
    </div>
  );
};
/* ─────────────────────────────────────────────
   Tooltip customizado padrão
───────────────────────────────────────────── */
function CustomTooltip({ active, payload, label, C, fmtVal = fmtBRL }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 8, padding: "8px 12px", fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    }}>
      <p style={{ fontWeight: 700, color: C.text, margin: "0 0 6px" }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, margin: "2px 0" }}>
          {p.name}: <strong>{typeof p.value === "number" ? fmtVal(p.value) : p.value}</strong>
        </p>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PÁGINA PRINCIPAL
═══════════════════════════════════════════════ */
export default function DadosLuciano() {
  const navigate = useNavigate();
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);

  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ projeto: [], tipoCusto: [], gp: [], mesAno: ['fevereiro/2026', 'janeiro/2026','março/2026'] });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ col: "PROJETO", dir: "asc" });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  /* ── Fetch ── */
  const fetchDados = async () => {
    setLoading(true); setError(null);
    console.log("📡 Iniciando requisição para o backend...");
    try {
      const result = await getDadosLuciano();
      if (!result.success) throw new Error(result.error || "Erro desconhecido ao buscar dados");
      setDados(result.data ?? []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchDados(); }, []);

  /* ── Opções dinâmicas ── */
  const opts = useMemo(() => ({
    projeto: [...new Set(dados.map((d) => d.PROJETO))].sort(),
    tipoCusto: [...new Set(dados.map((d) => d["TIPO CUSTO"]?.trim()).filter((tipo) => tipo && tipo !== "Custo total"))].sort(),
    gp: [...new Set(dados.map((d) => d.GP))].filter(Boolean).sort(),
    mesAno: [...new Set(dados.map((d) => d.MES_ANO))].sort((a, b) =>
      getValorCronologico(a) - getValorCronologico(b)
    ),
  }), [dados]);

  /* ── Pivot ── */
  const pivotado = useMemo(() => {
    const map = new Map();
    dados.forEach((d) => {
      const key = `${d.PROJETO}||${d["TIPO CUSTO"]}||${d.GP ?? ""}||${d.MES_ANO}`;
      if (!map.has(key)) map.set(key, { PROJETO: d.PROJETO, "TIPO CUSTO": d["TIPO CUSTO"], GP: d.GP ?? "—", MES_ANO: d.MES_ANO, PREVISTO: 0, REALIZADO: 0 });
      const row = map.get(key);
      const tipo = String(d.TIPO ?? "").trim().toLowerCase();
      let v = d.VALOR;
      if (typeof v === "string") v = Number(v.replace(/\./g, "").replace(",", ".")) || 0;
      else v = Number(v) || 0;
      if (tipo === "previsto") row.PREVISTO += v;
      if (tipo === "realizado") row.REALIZADO += v;
    });
    return Array.from(map.values()).map((r) => ({ ...r, DIFERENCA: r.PREVISTO - r.REALIZADO }));
  }, [dados]);

  /* ── Filtrado (tabela) ── */
  const NUM_COLS = ["PREVISTO", "REALIZADO", "DIFERENCA"];

  const filtrado = useMemo(() => {
    let r = pivotado.filter(d =>
      d["TIPO CUSTO"] && d["TIPO CUSTO"].trim().toLowerCase() !== "custo total"
    );

    if (filters.projeto.length) r = r.filter((d) => filters.projeto.includes(d.PROJETO));

    if (filters.tipoCusto.length) {
      r = r.filter((d) => filters.tipoCusto.includes(d["TIPO CUSTO"].trim()));
    }

    if (filters.gp.length) r = r.filter((d) => filters.gp.includes(d.GP));
    if (filters.mesAno.length) r = r.filter((d) => filters.mesAno.includes(d.MES_ANO));

    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((d) => Object.values(d).some((v) => String(v).toLowerCase().includes(q)));
    }

    return [...r].sort((a, b) => {
      let va = a[sort.col] ?? "";
      let vb = b[sort.col] ?? "";

      if (sort.col === "MES_ANO") {
        va = getValorCronologico(va);
        vb = getValorCronologico(vb);

        return sort.dir === "asc" ? va - vb : vb - va;
      }

      if (NUM_COLS.includes(sort.col)) {
        return sort.dir === "asc" ? va - vb : vb - va;
      }

      return sort.dir === "asc"
        ? String(va).localeCompare(String(vb), "pt-BR")
        : String(vb).localeCompare(String(va), "pt-BR");
    });
  }, [pivotado, filters, search, sort]);

  /* ── dadosParaKPIs (ignora filtro de tipoCusto) ── */
  const dadosParaKPIs = useMemo(() => {
    let r = pivotado;
    if (filters.projeto.length) r = r.filter((d) => filters.projeto.includes(d.PROJETO));
    if (filters.gp.length) r = r.filter((d) => filters.gp.includes(d.GP));
    if (filters.mesAno.length) r = r.filter((d) => filters.mesAno.includes(d.MES_ANO));
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((d) => Object.values(d).some((v) => String(v).toLowerCase().includes(q)));
    }
    return r;
  }, [pivotado, filters.projeto, filters.gp, filters.mesAno, search]);

  /* ── KPIs financeiros ── */
  const fatBrutoPrev = dadosParaKPIs.filter((d) => isReceita(d["TIPO CUSTO"])).reduce((s, d) => s + d.PREVISTO, 0);
  const fatBrutoReal = dadosParaKPIs.filter((d) => isReceita(d["TIPO CUSTO"])).reduce((s, d) => s + d.REALIZADO, 0);
  const fatLiqPrev = fatBrutoPrev * DEDUCAO;
  const fatLiqReal = fatBrutoReal * DEDUCAO;
  const custoTotalPrev = dadosParaKPIs.filter((d) => isCustoTot(d["TIPO CUSTO"])).reduce((s, d) => s + d.PREVISTO, 0);
  const custoTotalReal = dadosParaKPIs.filter((d) => isCustoTot(d["TIPO CUSTO"])).reduce((s, d) => s + d.REALIZADO, 0);
  const margemPrev = fatLiqPrev !== 0 ? (fatLiqPrev - custoTotalPrev) / fatLiqPrev : 0;
  const margemReal = fatLiqReal !== 0 ? (fatLiqReal - custoTotalReal) / fatLiqReal : 0;
  const totalPrevisto = filtrado.reduce((s, d) => s + d.PREVISTO, 0);
  const totalReal = filtrado.reduce((s, d) => s + d.REALIZADO, 0);
  const totalDif = totalReal - totalPrevisto;
  const activeFilters = Object.values(filters).flat().length;

  /* ══════════════════════════════════════════
     DADOS DOS GRÁFICOS
  ══════════════════════════════════════════ */

  /* 1. Waterfall de Receita (Realizado) */
  const margValorReal = fatLiqReal - custoTotalReal;
  const waterfallData = useMemo(() => [
    { name: "Fat. Bruto", phantom: 0, valor: fatBrutoReal, tipo: "neutro" },
    { name: "Deduções", phantom: fatLiqReal, valor: fatBrutoReal - fatLiqReal, tipo: "negativo" },
    { name: "Fat. Líquido", phantom: 0, valor: fatLiqReal, tipo: "neutro" },
    { name: "Custo Total", phantom: Math.max(0, margValorReal), valor: custoTotalReal, tipo: "negativo" },
    { name: "Margem Bruta", phantom: 0, valor: Math.abs(margValorReal), tipo: margValorReal >= 0 ? "positivo" : "negativo" },
  ], [fatBrutoReal, fatLiqReal, custoTotalReal, margValorReal]);

  /* 2. Scatter por Projeto */
  const scatterData = useMemo(() => {
    const map = new Map();
    dadosParaKPIs.forEach((d) => {
      if (!map.has(d.PROJETO)) map.set(d.PROJETO, { projeto: d.PROJETO, receita: 0, custo: 0 });
      const row = map.get(d.PROJETO);
      if (isReceita(d["TIPO CUSTO"])) row.receita += d.REALIZADO;
      if (isCustoTot(d["TIPO CUSTO"])) row.custo += d.REALIZADO;
    });
    return Array.from(map.values()).map((r) => {
      const liq = r.receita * DEDUCAO;
      return {
        projeto: r.projeto,
        x: r.receita,
        y: liq !== 0 ? ((liq - r.custo) / liq) * 100 : 0,
      };
    }).filter((d) => d.x > 0);
  }, [dadosParaKPIs]);

  /* 3. Barras por Mês */
  const porMesData = useMemo(() => {
    const map = new Map();
    dadosParaKPIs.forEach((d) => {
      if (!map.has(d.MES_ANO)) map.set(d.MES_ANO, { mes: d.MES_ANO, previsto: 0, realizado: 0 });
      const row = map.get(d.MES_ANO);
      row.previsto += d.PREVISTO;
      row.realizado += d.REALIZADO;
    });
    return Array.from(map.values()).sort((a, b) => getValorCronologico(a.mes) - getValorCronologico(b.mes));
  }, [dadosParaKPIs]);

  /* 4. Ranking de GP por Desvio de Margem */
  const gpRankingData = useMemo(() => {
    const map = new Map();
    dadosParaKPIs.forEach((d) => {
      const gp = d.GP && d.GP !== "—" ? d.GP : null;
      if (!gp) return;
      if (!map.has(gp)) map.set(gp, { gp, recP: 0, recR: 0, cusP: 0, cusR: 0 });
      const row = map.get(gp);
      if (isReceita(d["TIPO CUSTO"])) { row.recP += d.PREVISTO; row.recR += d.REALIZADO; }
      if (isCustoTot(d["TIPO CUSTO"])) { row.cusP += d.PREVISTO; row.cusR += d.REALIZADO; }
    });
    return Array.from(map.values()).map((r) => {
      const lP = r.recP * DEDUCAO; const lR = r.recR * DEDUCAO;
      const mP = lP !== 0 ? ((lP - r.cusP) / lP) * 100 : 0;
      const mR = lR !== 0 ? ((lR - r.cusR) / lR) * 100 : 0;
      return { gp: r.gp, margemPrev: parseFloat(mP.toFixed(2)), margemReal: parseFloat(mR.toFixed(2)), desvio: parseFloat((mR - mP).toFixed(2)) };
    }).sort((a, b) => a.desvio - b.desvio);
  }, [dadosParaKPIs]);


  /* 6. Gauge de Eficiência */
  const eficiencia = totalPrevisto !== 0 ? (totalReal / totalPrevisto) * 100 : 0;

  /* 7. Tornado por Projeto */
  const tornadoData = useMemo(() => {
      const map = new Map();
      dadosParaKPIs.forEach((d) => {
        if (d['TIPO CUSTO'] !== " Receita ") {
          return; 
        }

        if (!map.has(d.PROJETO)) {
          map.set(d.PROJETO, { projeto: d.PROJETO, prev: 0, real: 0 });
        }
        
        const row = map.get(d.PROJETO);
        row.prev += (d.PREVISTO || 0); 
        row.real += (d.REALIZADO || 0);
      });

      return Array.from(map.values())
        .map((r) => ({ 
          projeto: r.projeto, 
          desvio: r.real - r.prev, 
          previsto: r.prev, 
          realizado: r.real 
        }))
        .sort((a, b) => Math.abs(b.desvio) - Math.abs(a.desvio));
    }, [dadosParaKPIs]);

  /* 8. Evolução da Margem (Linha) */
  const margemPorMesData = useMemo(() => {
    const map = new Map();
    dadosParaKPIs.forEach((d) => {
      if (!map.has(d.MES_ANO)) map.set(d.MES_ANO, { mes: d.MES_ANO, rec: 0, cus: 0, recP: 0, cusP: 0 });
      const row = map.get(d.MES_ANO);
      if (isReceita(d["TIPO CUSTO"])) { row.rec += d.REALIZADO; row.recP += d.PREVISTO; }
      if (isCustoTot(d["TIPO CUSTO"])) { row.cus += d.REALIZADO; row.cusP += d.PREVISTO; }
    });
    return Array.from(map.values()).map(r => {
      const liqR = r.rec * DEDUCAO;
      const liqP = r.recP * DEDUCAO;
      const margemR = liqR !== 0 ? ((liqR - r.cus) / liqR) * 100 : 0;
      const margemP = liqP !== 0 ? ((liqP - r.cusP) / liqP) * 100 : 0;
      return { mes: r.mes, margemReal: parseFloat(margemR.toFixed(1)), margemPrev: parseFloat(margemP.toFixed(1)) };
    }).sort((a, b) => getValorCronologico(a.mes) - getValorCronologico(b.mes));
  }, [dadosParaKPIs]);


  /* 10. Top 5 Margem % (Comparativo) */
  const top5MargemPct = useMemo(() => {
    const map = new Map();
    dadosParaKPIs.forEach((d) => {
      if (!map.has(d.PROJETO)) map.set(d.PROJETO, { projeto: d.PROJETO, recP: 0, recR: 0, cusP: 0, cusR: 0 });
      const row = map.get(d.PROJETO);
      if (isReceita(d["TIPO CUSTO"])) { row.recP += d.PREVISTO; row.recR += d.REALIZADO; }
      if (isCustoTot(d["TIPO CUSTO"])) { row.cusP += d.PREVISTO; row.cusR += d.REALIZADO; }
    });

    return Array.from(map.values())
      .map((r) => {
        const liqP = r.recP * DEDUCAO;
        const liqR = r.recR * DEDUCAO;
        return {
          projeto: r.projeto,
          mbProj: liqP !== 0 ? parseFloat(((liqP - r.cusP) / liqP * 100).toFixed(1)) : 0,
          mbPct: liqR !== 0 ? parseFloat(((liqR - r.cusR) / liqR * 100).toFixed(1)) : 0,
        };
      })
      .filter(d => d.mbPct !== 0 || d.mbProj !== 0)
      .sort((a, b) => b.mbPct - a.mbPct)
      .slice(0, 15)
  }, [dadosParaKPIs]);

  /* ── Paginação ── */
  const totalPages = Math.max(1, Math.ceil(filtrado.length / PAGE_SIZE));
  const pageData = filtrado.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (col) => { setSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" })); setPage(1); };
  const limpar = () => { setFilters({ projeto: [], tipoCusto: [], gp: [], mesAno: [] }); setSearch(""); setPage(1); };

  const cssVars = { "--bg": C.bg, "--card": C.card, "--border": C.border, "--text": C.text, "--muted": C.muted };

  /* Cores recharts */
  const CPREV = "#A78BFA";  // roxo claro — previsto
  const CREAL = "#7C3AED";  // roxo escuro — realizado
  const CGOOD = "#10B981";
  const CBAD = "#EF4444";
  const CGRID = isLight ? "#E5E0F0" : "#2E2050";
  const CTXT = C.muted;

  const axisStyle = { fontSize: 11, fill: CTXT };
  const tooltipStyle = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 };
  const location = useLocation();
  const isDashRoute = location.pathname.includes("-dash");
  /* ─────────────────────── JSX ─────────────────────────── */
  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        background: C.bg,
        fontFamily: "'DM Sans', sans-serif",
        color: C.text,
      }}
    >
      {isDashRoute ? (
        <SideBarNavOperacao isLight={isLight} />
      ) : (
        <SideBarNavOperacao isLight={isLight} />
      )}

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
      {/* Header */}
      <header style={{
        background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
        color: "#fff", padding: "14px 24px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
          <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => setIsLight((l) => !l)} style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
          }}>
            {isLight ? <Sun size={14} /> : <Moon size={14} />}
            {isLight ? "Tema Claro" : "Tema Escuro"}
          </button>
          <button onClick={() => navigate("/inicio")} style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
          }}>
            Menu Principal
          </button>
        </div>
      </header>

      {/* Body */}
      <div style={{ padding: "24px" }}>
        <div style={{ maxWidth: 1750, margin: "0 auto" }}>

          {/* Título */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 4, height: 28, background: C.brand, borderRadius: 2 }} />
              <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: 0 }}>Performance Operacional: Planejado x Realizado</h1>
            </div>
          </div>

          {/* Erro */}
          {error && (
            <div style={{
              background: isLight ? "#FFFBEB" : "#451A03", border: "1px solid #F59E0B",
              borderRadius: 8, padding: "12px 16px", marginBottom: 20,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <AlertCircle size={18} color="#D97706" />
              <p style={{ fontSize: 13, color: isLight ? "#92400E" : "#FCD34D", margin: 0 }}>
                <strong>Atenção:</strong> {error}
              </p>
            </div>
          )}

          {/* KPI Cards — Planejado */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <div style={{ width: 3, height: 14, background: C.muted, borderRadius: 2, opacity: 0.5 }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>Planejado</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 12 }}>
            {[
              { label: "Fat. Bruto Planejado", val: fmtBRL(fatBrutoPrev), sub: "Soma Receita", color: C.muted },
              { label: "Fat. Líquido Planejado", val: fmtBRL(fatLiqPrev), sub: "Fat. Bruto × 93,35%", color: C.muted },
              { label: "Custo Total Planejado", val: fmtBRL(custoTotalPrev), sub: "Soma Custos", color: C.danger },
              { label: "Margem Bruta Planejada", val: fmtPct(margemPrev), sub: "(Fat. Líq. − Custo) / Fat. Líq.", color: margemPrev >= 0 ? "#7C3AED" : CBAD },

            ].map((k) => (
              <div key={k.label} style={{
                background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
                overflow: "hidden",
                boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
              }}>
                {loading ? <CardSkeleton isLight={isLight} /> : (
                  <div style={{ padding: "14px 18px" }}>
                    <p style={{ fontSize: 11, color: C.muted, margin: "0 0 4px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{k.label}</p>
                    <p style={{ fontSize: 20, fontWeight: 800, color: k.color, margin: "0 0 2px" }}>{k.val}</p>
                    <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>{k.sub}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* KPI Cards — Realizado */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <div style={{ width: 3, height: 14, background: C.brand, borderRadius: 2 }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: C.brand, textTransform: "uppercase", letterSpacing: "0.07em" }}>Realizado</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
            {[
              { label: "Fat. Bruto Realizado", val: fmtBRL(fatBrutoReal), sub: "Soma Receita", color: "#7C3AED" },
              { label: "Fat. Líquido Realizado", val: fmtBRL(fatLiqReal), sub: "Fat. Bruto × 93,35%", color: "#7C3AED" },
              { label: "Custo Total Realizado", val: fmtBRL(custoTotalReal), sub: "Soma Custos", color: C.danger },
              { label: "Margem Bruta Realizada", val: fmtPct(margemReal), sub: "(Fat. Líq. − Custo) / Fat. Líq.", color: margemReal >= 0 ? CGOOD : CBAD },
            ].map((k) => (
              <div key={k.label} style={{
                background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
                overflow: "hidden",
                boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
              }}>
                {loading ? <CardSkeleton isLight={isLight} /> : (
                  <div style={{ padding: "14px 18px" }}>
                    <p style={{ fontSize: 11, color: C.muted, margin: "0 0 4px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{k.label}</p>
                    <p style={{ fontSize: 20, fontWeight: 800, color: k.color, margin: "0 0 2px" }}>{k.val}</p>
                    <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>{k.sub}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* ══════ FILTROS ══════ */}
          <div style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: 16, marginBottom: 20,
            boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Filter size={14} color={C.muted} />
              <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Filtros</span>
              {activeFilters > 0 && (
                <span style={{ background: C.brand, color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "1px 7px" }}>
                  {activeFilters} ativo(s)
                </span>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 12 }}>
              {[
                { key: "projeto", label: "Projeto", opts: opts.projeto },
                { key: "tipoCusto", label: "Tipo Custo", opts: opts.tipoCusto },
                { key: "gp", label: "GP", opts: opts.gp },
                { key: "mesAno", label: "Mês / Ano", opts: opts.mesAno },
              ].map(({ key, label, opts: o }) => (
                <div key={key}>
                  <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, fontWeight: 600 }}>{label}</label>
                  <MultiSelect options={o} placeholder={label} value={filters[key]}
                    onChange={(v) => { setFilters((f) => ({ ...f, [key]: v })); setPage(1); }} />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                flex: 1, display: "flex", alignItems: "center", gap: 8,
                border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 10px",
              }}>
                <Search size={13} color={C.muted} />
                <input value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Busca livre em todos os campos…"
                  style={{ border: "none", outline: "none", background: "transparent", fontSize: 12, color: C.text, width: "100%" }} />
                {search && (
                  <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                    <X size={13} color={C.muted} />
                  </button>
                )}
              </div>
              {(activeFilters > 0 || search) && (
                <button onClick={limpar} style={{ fontSize: 13, color: C.brand, fontWeight: 700, background: "none", border: "none", cursor: "pointer", padding: 0, whiteSpace: "nowrap" }}>
                  Limpar Filtros
                </button>
              )}
            </div>
          </div>

          {/* ══════════════════════════════════════════
              SEÇÃO DE GRÁFICOS
          ══════════════════════════════════════════ */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14 }}>
            <div style={{ width: 4, height: 18, background: C.brand, borderRadius: 2 }} />
            <span style={{ fontSize: 13, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: "0.06em" }}>Análise Gráfica</span>
          </div>

          {/* Linha 1 — Waterfall + Barras por Mês */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>

            {/* 1. Waterfall */}
            <ChartCard C={C} isLight={isLight} loading={loading}
              title="Cascata de Receita (Realizado)"
              subtitle="Caminho do Faturamento Bruto até a Margem Bruta">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={waterfallData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CGRID} vertical={false} />
                  <XAxis dataKey="name" tick={axisStyle} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtK} tick={axisStyle} axisLine={false} tickLine={false} width={65} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const val = payload.find((p) => p.dataKey === "valor");
                      return (
                        <div style={{ ...tooltipStyle, padding: "8px 12px" }}>
                          <p style={{ color: C.text, fontWeight: 700, margin: "0 0 4px" }}>{label}</p>
                          <p style={{ color: val?.fill ?? C.text, margin: 0 }}>{fmtBRL(val?.value ?? 0)}</p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="phantom" stackId="wf" fill="transparent" stroke="none" legendType="none" />
                  <Bar dataKey="valor" stackId="wf" radius={[4, 4, 0, 0]}>
                    {waterfallData.map((entry, i) => (
                      <Cell key={i} fill={
                        entry.tipo === "positivo" ? CGOOD :
                          entry.tipo === "negativo" ? CBAD : CREAL
                      } />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* 3. Barras por Mês */}
            <ChartCard C={C} isLight={isLight} loading={loading}
              title="Realizado × Planejado por Mês"
              subtitle="Assertividade do orçamento ao longo do tempo">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={porMesData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CGRID} vertical={false} />
                  <XAxis dataKey="mes" tick={{ ...axisStyle, fontSize: 10 }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => v.replace("/", "\n")} />
                  <YAxis tickFormatter={fmtK} tick={axisStyle} axisLine={false} tickLine={false} width={65} />
                  <Tooltip content={(props) => <CustomTooltip {...props} C={C} fmtVal={fmtBRL} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="previsto" name="Planejado" fill={CPREV} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="realizado" name="Realizado" fill={CREAL} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Linha 2 — Scatter + GP Ranking */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>

            {/* 2. Scatter / Quadrant */}
            <ChartCard C={C} isLight={isLight} loading={loading}
              title="Quadrant Chart — Projetos"
              subtitle="Eixo X: Fat. Bruto Realizado · Eixo Y: % Margem Bruta">
              <ResponsiveContainer width="100%" height={260}>
                <ScatterChart margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CGRID} />
                  <XAxis dataKey="x" name="Fat. Realizado" type="number" tickFormatter={fmtK}
                    tick={axisStyle} axisLine={false} tickLine={false} label={{ value: "Fat. Realizado", position: "insideBottom", offset: -2, style: { fill: CTXT, fontSize: 10 } }} />
                  <YAxis dataKey="y" name="Margem %" type="number" tickFormatter={(v) => `${v.toFixed()}%`}
                    tick={axisStyle} axisLine={false} tickLine={false} />
                  <ZAxis range={[60, 60]} />
                  <ReferenceLine y={60} stroke={CBAD} strokeDasharray="4 4" strokeWidth={1.5} />
                  <Tooltip cursor={{ strokeDasharray: "3 3" }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div style={{ ...tooltipStyle, padding: "8px 12px" }}>
                          <p style={{ color: C.text, fontWeight: 700, margin: "0 0 4px", fontSize: 11, maxWidth: 180 }}>{d?.projeto}</p>
                          <p style={{ color: C.muted, margin: "2px 0", fontSize: 11 }}>Fat.: {fmtBRL(d?.x)}</p>
                          <p style={{ color: d?.y >= 0 ? CGOOD : CBAD, margin: "2px 0", fontSize: 11, fontWeight: 700 }}>Margem: {d?.y?.toFixed(1)}%</p>
                        </div>
                      );
                    }}
                  />
                  <Scatter name="Projetos" data={scatterData}>
                    {scatterData.map((entry, i) => (
                      <Cell key={i} fill={entry.y >= 60 ? CGOOD : entry.y >= 0 ? CPREV : CBAD} fillOpacity={0.85} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", gap: 16, marginTop: 4, flexWrap: "wrap" }}>
                {[
                  { label: "Estrela (≥ 60%)", color: CGOOD },
                  { label: "Neutro (0–60%)", color: CPREV },
                  { label: "Dreno (< 0%)", color: CBAD },
                ].map((l) => (
                  <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: l.color }} />
                    <span style={{ fontSize: 10, color: C.muted }}>{l.label}</span>
                  </div>
                ))}
              </div>
            </ChartCard>

            {/* 4. GP Ranking por Desvio */}
            <ChartCard C={C} isLight={isLight} loading={loading}
              title="Ranking de GP por Desvio de Margem"
              subtitle="Diferença entre Margem Planejada e Realizada (pp)">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={gpRankingData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CGRID} horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)}pp`}
                    tick={axisStyle} axisLine={false} tickLine={false} />
                  <YAxis dataKey="gp" type="category" width={80} tick={{ ...axisStyle, fontSize: 11 }}
                    axisLine={false} tickLine={false} />
                  <ReferenceLine x={0} stroke={C.border} strokeWidth={1.5} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    return (
                      <div style={{ ...tooltipStyle, padding: "8px 12px" }}>
                        <p style={{ color: C.text, fontWeight: 700, margin: "0 0 4px" }}>{label}</p>
                        <p style={{ color: C.muted, margin: "2px 0", fontSize: 11 }}>Planejado: {d?.margemPrev?.toFixed(1)}%</p>
                        <p style={{ color: C.muted, margin: "2px 0", fontSize: 11 }}>Realizado: {d?.margemReal?.toFixed(1)}%</p>
                        <p style={{ color: d?.desvio >= 0 ? CGOOD : CBAD, margin: "4px 0 0", fontWeight: 700 }}>
                          Desvio: {d?.desvio >= 0 ? "+" : ""}{d?.desvio?.toFixed(1)} pp
                        </p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="desvio" name="Desvio (pp)" radius={[0, 4, 4, 0]}>
                    {gpRankingData.map((entry, i) => (
                      <Cell key={i} fill={entry.desvio >= 0 ? CGOOD : CBAD} />
                    ))}
                    <LabelList dataKey="desvio" position="right"
                      style={{ fontSize: 10, fontWeight: 700, fill: C.muted }}
                      formatter={(v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}pp`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Linha 3 — Gauge + Tornado */}
          <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 14, marginBottom: 14 }}>

            {/* 6. Gauge */}
            <ChartCard C={C} isLight={isLight} loading={loading}
              title="Performance de Planejamento"
              subtitle="Realizado / Planejado (todos os tipos de custo)">
              <GaugeChart pct={eficiencia} C={C} />
            </ChartCard>

            {/* 7. Tornado Chart */}
            <ChartCard C={C} isLight={isLight} loading={loading}
              title="Tornado — Desvio por Projeto"
              subtitle="Top 10 maiores desvios (Realizado − Planejado) em R$">

              {/* Container com Scroll: Definimos a altura máxima aqui */}
              <div style={{ height: 300, overflowY: "auto", overflowX: "hidden", paddingRight: 8 }}>

                {/* Calculamos a altura: 40px por barra + margens */}
                <div style={{ height: Math.max(tornadoData.length * 40, 260) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={tornadoData}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                      barSize={25}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={CGRID} horizontal={false} />

                      <XAxis
                        type="number"
                        tickFormatter={fmtK}
                        tick={axisStyle}
                        axisLine={false}
                        tickLine={false}
                        domain={['auto', 'auto']} // Garante que o zero fique centralizado se houver negativos
                      />

                      <YAxis
                        dataKey="projeto"
                        type="category"
                        width={130}
                        tick={{ ...axisStyle, fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => v.length > 20 ? v.slice(0, 20) + "…" : v}
                      />

                      <ReferenceLine x={0} stroke={C.border} strokeWidth={1.5} />

                      <Tooltip
                        cursor={{ fill: 'transparent' }} // Remove o retângulo cinza ao passar o mouse
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0]?.payload;
                          return (
                            <div style={{ ...tooltipStyle, padding: "8px 12px", border: `1px solid ${C.border}` }}>
                              <p style={{ color: C.text, fontWeight: 700, margin: "0 0 4px", fontSize: 11, maxWidth: 200 }}>{label}</p>
                              <p style={{ color: C.muted, margin: "2px 0", fontSize: 11 }}>Planejado: {fmtBRL(d?.previsto)}</p>
                              <p style={{ color: C.muted, margin: "2px 0", fontSize: 11 }}>Realizado: {fmtBRL(d?.realizado)}</p>
                              <p style={{ color: d?.desvio >= 0 ? CGOOD : CBAD, margin: "4px 0 0", fontWeight: 700 }}>
                                Desvio: {fmtBRL(d?.desvio)}
                              </p>
                            </div>
                          );
                        }}
                      />
                      <Bar dataKey="desvio" name="Desvio" radius={[0, 4, 4, 0]}>
                        {tornadoData.map((entry, i) => (
                          <Cell key={i} fill={entry.desvio >= 0 ? CGOOD : CBAD} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ChartCard>
          </div>
          {/* Linha 4 — Evolução da Margem + Composição de Custos */}
          <div style={{ display: "grid", gap: 14, marginBottom: 20 }}>

            {/* 8. Evolução da Margem % */}
            <ChartCard C={C} isLight={isLight} loading={loading}
              title="Evolução da Margem Bruta (%)"
              subtitle="Acompanhamento da margem percentual ao longo dos meses">
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={margemPorMesData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CGRID} vertical={false} />
                  <XAxis dataKey="mes" tick={{ ...axisStyle, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => v.replace("/", "\n")} />
                  <YAxis tickFormatter={(v) => `${v}%`} tick={axisStyle} axisLine={false} tickLine={false} width={40} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div style={{ ...tooltipStyle, padding: "8px 12px" }}>
                        <p style={{ color: C.text, fontWeight: 700, margin: "0 0 4px" }}>{label}</p>
                        {payload.map(p => (
                          <p key={p.dataKey} style={{ color: p.color, margin: "2px 0", fontSize: 11 }}>
                            {p.name}: <strong>{p.value}%</strong>
                          </p>
                        ))}
                      </div>
                    );
                  }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="margemPrev" name="Margem Planejada" stroke={CPREV} strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="margemReal" name="Margem Realizada" stroke={CREAL} strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          <ChartCard C={C} isLight={isLight} loading={loading}
            title="Margem por Projeto — Comparativo %"
            subtitle="Margem Real vs. Margem Projetada (Role para baixo)">

            {/* Container com altura fixa e scroll */}
            <div style={{
              height: 500,
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: 8,
              marginTop: 10
            }}>

              <ResponsiveContainer
                width="100%"
                /* Calculamos a altura total: número de itens * 60 pixels de espaço */
                height={Math.max(400, top5MargemPct.length * 60)}
              >
                <BarChart
                  data={top5MargemPct}
                  layout="vertical"
                  margin={{ left: 30, right: 60, top: 20, bottom: 20 }}
                  barGap={2}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={CGRID} horizontal={true} vertical={false} />

                  <XAxis type="number" tickFormatter={v => `${v}%`} tick={axisStyle} axisLine={false} />

                  <YAxis
                    type="category"
                    dataKey="projeto"
                    tick={{ ...axisStyle, fontSize: 10, fontWeight: 600 }}
                    width={150} // Aumentado para o nome do projeto não sumir
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip cursor={{ fill: 'rgba(124,58,237,0.05)' }} content={(props) => <CustomTooltip {...props} C={C} fmtVal={(v) => `${v}%`} />} />

                  <Legend verticalAlign="top" align="right" wrapperStyle={{ fontSize: 11, paddingBottom: 20 }} />

                  {/* Barra de Margem Projetada - barSize aumentado para 18 */}
                  <Bar dataKey="mbProj" name="Margem Projetada %" fill={CPREV} radius={[0, 4, 4, 0]} barSize={18}>
                    <LabelList dataKey="mbProj" position="right" formatter={v => `${v}%`} style={{ fontSize: 10, fill: C.muted, fontWeight: 500 }} />
                  </Bar>

                  {/* Barra de Margem Real - barSize aumentado para 18 */}
                  <Bar dataKey="mbPct" name="Margem Real %" fill={CREAL} radius={[0, 4, 4, 0]} barSize={18}>
                    <LabelList dataKey="mbPct" position="right" formatter={v => `${v}%`} style={{ fontSize: 10, fill: CREAL, fontWeight: 800 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

          </ChartCard>

          {/* ══════ TABELA ══════ */}
          <div style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 10, overflow: "hidden",
            boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
            marginTop: 20
          }}>

            {loading ? (
              <div style={{ padding: 60, textAlign: "center", color: C.muted, fontSize: 14 }}>
                <RefreshCw size={20} style={{ animation: "spin 1s linear infinite", marginBottom: 10 }} /><br />Carregando dados…
              </div>
            ) : filtrado.length === 0 ? (
              <div style={{ padding: 60, textAlign: "center", color: C.muted, fontSize: 14 }}>
                Nenhum registro encontrado com os filtros aplicados.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, margin: 14 }}>
                      <div style={{ width: 4, height: 18, background: C.brand, borderRadius: 2 }} />
                      <span style={{ fontSize: 13, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: "0.06em" }}>Análise Detalhada</span>
                    </div>
                    <tr>
                      <ThSort label="Projeto" col="PROJETO" sort={sort} onSort={handleSort} />
                      <ThSort label="Tipo Custo" col="TIPO CUSTO" sort={sort} onSort={handleSort} />
                      <ThSort label="GP" col="GP" sort={sort} onSort={handleSort} />
                      <ThSort label="Mês / Ano" col="MES_ANO" sort={sort} onSort={handleSort} />
                      <ThSort label="Valor Planejado" col="PLANEJADO" sort={sort} onSort={handleSort} align="right" />
                      <ThSort label="Valor Realizado" col="REALIZADO" sort={sort} onSort={handleSort} align="right" />
                      <ThSort label="Diferença" col="DIFERENCA" sort={sort} onSort={handleSort} align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((row, i) => (
                      <tr key={i}
                        style={{ background: i % 2 === 0 ? C.row1 : C.row2, transition: "background 0.15s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = isLight ? "#EDE9FA" : "#231545")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? C.row1 : C.row2)}
                      >
                        <td style={{ padding: "10px 14px", fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}`, fontWeight: 600, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.PROJETO}</td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: C.muted, borderBottom: `1px solid ${C.border}` }}>{row["TIPO CUSTO"]}</td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: C.text, borderBottom: `1px solid ${C.border}` }}>{row.GP}</td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: C.text, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{row.MES_ANO}</td>
                        <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 600, borderBottom: `1px solid ${C.border}`, textAlign: "right", color: C.muted }}>{fmtBRL(row.PREVISTO)}</td>
                        <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, borderBottom: `1px solid ${C.border}`, textAlign: "right", color: "#7C3AED" }}>{fmtBRL(row.REALIZADO)}</td>
                        <td style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "right" }}><DifCell val={row.DIFERENCA} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: isLight ? "#EDE9FA" : "#231545" }}>
                      <td colSpan={4} style={{ padding: "10px 14px", fontSize: 12, fontWeight: 700, color: C.muted, borderTop: `2px solid ${C.border}` }}>
                        TOTAIS — {filtrado.length} registros
                      </td>
                      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 800, color: C.text, borderTop: `2px solid ${C.border}`, textAlign: "right" }}>{fmtBRL(totalPrevisto)}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 800, color: "#7C3AED", borderTop: `2px solid ${C.border}`, textAlign: "right" }}>{fmtBRL(totalReal)}</td>
                      <td style={{ padding: "10px 14px", borderTop: `2px solid ${C.border}`, textAlign: "right" }}><DifCell val={totalDif} /></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Paginação */}
            {!loading && filtrado.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 12, color: C.muted }}>
                  Exibindo <strong style={{ color: C.text }}>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtrado.length)}</strong> de{" "}
                  <strong style={{ color: C.text }}>{filtrado.length}</strong> registros
                </span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {[
                    { label: "«", action: () => setPage(1), disabled: page === 1 },
                    { label: "‹", action: () => setPage((p) => p - 1), disabled: page === 1 },
                    { label: "›", action: () => setPage((p) => p + 1), disabled: page === totalPages },
                    { label: "»", action: () => setPage(totalPages), disabled: page === totalPages },
                  ].map((btn) => (
                    <button key={btn.label} onClick={btn.action} disabled={btn.disabled} style={{
                      padding: "5px 10px", borderRadius: 6, fontSize: 13,
                      border: `1px solid ${C.border}`,
                      background: btn.disabled ? "transparent" : C.brand,
                      color: btn.disabled ? C.muted : "#fff",
                      cursor: btn.disabled ? "not-allowed" : "pointer", fontWeight: 700,
                    }}>{btn.label}</button>
                  ))}
                  <span style={{ fontSize: 12, color: C.muted, marginLeft: 4 }}>Pág. {page}/{totalPages}</span>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      `}</style>
    </div>
    </div>
  );
}